import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {ENTITLEMENT_KEYS} from '@packages/domains/billing/constants';

export const canAccessSeo = defineAbilityConfig({
  entitlements: [ENTITLEMENT_KEYS.ALLOW_SEO],
} as const);

export const canViewAeoSiteAudit = defineAbilityConfig([
  {
    abilities: {canAccessAIFeatures: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_SITE_AUDIT]: true,
    },
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_RECOMMENDATIONS],
    permission: ['read', 'siteAeoRecommendations'],
  } as const,
  {
    abilities: {isAIToggleEnabled: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_SITE_AUDIT]: true,
    },
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_RECOMMENDATIONS],
    permission: ['read', 'siteAeoRecommendations'],
    isReadOnly: true,
  } as const,
]);

export const canViewSiteRecommendationConfiguration = defineAbilityConfig({
  abilities: {canViewAeoSiteAudit: true},
} as const);

export const canUpdateSiteRecommendationConfiguration = defineAbilityConfig({
  abilities: {canViewSiteRecommendationConfiguration: true},
  permission: ['manage', 'siteAeoRecommendations'],
} as const);

export const canManageAeoRecommendations = defineAbilityConfig({
  abilities: {canViewAeoSiteAudit: true},
  permission: ['manage', 'siteAeoRecommendations'],
  workflows: {optimization: true, preview: false},
} as const);

export const canAccessAeoSiteAudit = defineAbilityConfig({
  abilities: {canViewAeoSiteAudit: true},
  visiblePanels: ['aeo-recommendations'],
} as const);

export const canViewAeoRecommendationFeedback = defineAbilityConfig({
  abilities: {canViewAeoSiteAudit: true},
  featureFlags: {
    [FEATURE_FLAGS.AEO_RECOMMENDATION_FEEDBACK]: true,
  },
} as const);

export const canUseAgentInstructionsForContentRecommendations =
  defineAbilityConfig({
    abilities: {canViewAeoSiteAudit: true, canReadAgentInstructions: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_AGENT_INSTRUCTIONS_FOR_CONTENT_RECOMMENDATIONS]: true,
    },
  } as const);

export const canAccessAeoSiteAuditLocalization = defineAbilityConfig({
  featureFlags: {
    [FEATURE_FLAGS.AEO_SITE_AUDIT_LOCALIZATION]: true,
  },
} as const);

export const canAccessAeoContentAuthoring = defineAbilityConfig([
  {
    abilities: {canAccessAIFeatures: true},
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    featureFlags: {
      [FEATURE_FLAGS.AEO_CONTENT_AUTHORING_WORKFLOWS]: true,
    },
  } as const,
  // Bypass entitlement check for internal testing
  {
    abilities: {canAccessAIFeatures: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_CONTENT_AUTHORING_WORKFLOWS]: true,
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
  } as const,
]);

/** Gates the content workflow toggle on the AEO Analytics entitlement. AEO-532. */
export const canAccessAeoContentWorkflowToggle = defineAbilityConfig({
  abilities: {canAccessAeoContentAuthoring: true},
  entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
} as const);

export const canViewAeoMeasurement = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
    },
  } as const,
  // Bypass entitlement check for internal testing
  {
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
  } as const,
]);

export const canManageAeoAnalytics = defineAbilityConfig([
  {
    abilities: {canAccessAIFeatures: true},
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
    },
    permission: ['manage', 'siteAeoAnalytics'],
  } as const,
  // Bypass entitlement check for internal testing (still requires permission)
  {
    abilities: {canAccessAIFeatures: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
    permission: ['manage', 'siteAeoAnalytics'],
  } as const,
]);

export const canViewAeoBrand = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
  } as const,
  // Bypass entitlement check for internal testing
  {
    featureFlags: {
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
  } as const,
]);

export const canManageAeoBrand = defineAbilityConfig([
  {
    abilities: {canAccessAIFeatures: true},
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    permission: ['manage', 'siteAeoBrand'],
  } as const,
  // Bypass entitlement check for internal testing (still requires permission)
  {
    abilities: {canAccessAIFeatures: true},
    featureFlags: {
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
    permission: ['manage', 'siteAeoBrand'],
  } as const,
]);

export const canViewAeoLLMConfig = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
    },
  } as const,
  // Bypass entitlement check for internal testing
  {
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
  } as const,
]);

export const canManageAeoLLMConfig = defineAbilityConfig([
  {
    entitlements: [ENTITLEMENT_KEYS.ALLOW_AEO_ANALYTICS],
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
    },
    permission: ['manage', 'siteAeoLLMConfig'],
  } as const,
  // Bypass entitlement check for internal testing (still requires permission)
  {
    featureFlags: {
      [FEATURE_FLAGS.AEO_MEASUREMENT]: true,
      [FEATURE_FLAGS.AEO_NO_ENTITLEMENTS]: true,
    },
    permission: ['manage', 'siteAeoLLMConfig'],
  } as const,
]);
